import type { EmployeeClockState } from './timeClockActions'
import { isFieldCompleteStatus } from './jobStatusWorkflow'

export type MyDayWorkflowAction = 'start' | 'resume' | 'checklist' | 'complete' | null

export interface MyDayWorkflowButton {
  action: Exclude<MyDayWorkflowAction, null>
  label: string
  disabled?: boolean
  variant: 'success' | 'primary'
}

export function isJobDoneStatus(status: string): boolean {
  return isFieldCompleteStatus(status)
}

/** True when job is still in_progress but this employee has no open job time segment on it. */
export function isMyDayJobClockPaused(
  jobStatus: string,
  jobId: string,
  clock: EmployeeClockState
): boolean {
  return jobStatus === 'in_progress' && clock.jobEntry?.job_id !== jobId
}

/**
 * Single primary workflow button for a My Day job card.
 * - Start: not started (clocks day + starts job, or ends travel + starts job)
 * - Resume: in_progress but no open job time segment (after day punch-out unfinished)
 * - Checklist: actively on job with incomplete checklist
 * - Complete: actively on job with checklist fully done
 */
export function resolveMyDayWorkflowButton(params: {
  jobStatus: string
  jobId: string
  checklistComplete: boolean
  clock: EmployeeClockState
  /** Another assigned job still in_progress — disables Start on this card. */
  blockingJobId?: string | null
  /**
   * True when clock.jobEntry belongs to a finished job (pending_review / under_review / etc).
   * Leftover job punches must not grey out Start on other jobs.
   */
  clockJobEntryJobDone?: boolean
  /**
   * When false (default), Start stays enabled even if another job is in_progress.
   * Re-enable via tenants.settings.require_finish_job_before_start_next = true.
   */
  requireFinishBeforeStart?: boolean
}): MyDayWorkflowButton | null {
  const {
    jobStatus,
    jobId,
    checklistComplete,
    clock,
    blockingJobId,
    clockJobEntryJobDone,
    requireFinishBeforeStart = false,
  } = params
  if (isJobDoneStatus(jobStatus) || jobStatus === 'cancelled') return null

  if (jobStatus === 'in_progress') {
    const activeOnThisJob = clock.jobEntry?.job_id === jobId
    if (!activeOnThisJob) {
      return { action: 'resume', label: 'Resume', variant: 'success' }
    }
    if (checklistComplete) {
      return { action: 'complete', label: 'Complete', variant: 'primary' }
    }
    return { action: 'checklist', label: 'Checklist', variant: 'primary' }
  }

  const liveJobEntry = clockJobEntryJobDone ? null : clock.jobEntry
  const otherJobActive =
    requireFinishBeforeStart &&
    ((!!liveJobEntry && liveJobEntry.job_id !== jobId) ||
      (!!blockingJobId && blockingJobId !== jobId))
  return {
    action: 'start',
    label: 'Start',
    variant: 'success',
    disabled: otherJobActive,
  }
}

export const MY_DAY_TRAVEL_IDLE_MINUTES = 30
